import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  PackConfigGroup,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { GemsBalance } from '@sorare/core/src/components/commonGame/GemsBalance';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { groupBy } from '@sorare/core/src/lib/arrays';

import { DailyDeals } from 'components/commonCard/BuyPacks/DailyDeals';

import type {
  CommonPacksPage_marketRoot,
  CommonPacksPage_tokenRoot,
} from './__generated__/index.graphql';
import { CardPackGroup } from '../BuyPacks/CardPackGroup';
import { CommonPacksHeader } from '../BuyPacks/CommonPacksHeader';
import { DailyDealsTitle } from '../BuyPacks/DailyDeals/DailyDealsTitle';
import { PackOpeningDialog } from '../BuyPacks/PackOpeningDialog';
import { usePackOpeningDialog } from '../BuyPacks/PackOpeningDialog/usePackOpeningDialog';
import { usePackAssets } from '../PacksTypePage/usePackAssets';

type Props = {
  refetch: () => Promise<unknown>;
  marketRoot: CommonPacksPage_marketRoot;
  tokenRoot: CommonPacksPage_tokenRoot;
};
export const CommonPacksPage = ({ refetch, marketRoot, tokenRoot }: Props) => {
  const {
    flags: { usePurchasableItemId = false },
  } = useFeatureFlags();
  const gemsPacks = usePurchasableItemId
    ? tokenRoot?.purchasableItems
    : marketRoot?.gemsPacks || [];
  const { currentUser } = useCurrentUserContext();
  const { cardPackGroups } = marketRoot;
  const { openedCardPack, onPackSelect, onPackOpeningDialogClose } =
    usePackOpeningDialog(cardPackGroups);

  const { dailyDealCardPackGroups, marketCardPackGroups } = groupBy(
    marketRoot.cardPackGroups,
    cardPackGroup => {
      if (cardPackGroup.packConfigGroupType === PackConfigGroup.DAILY_DEAL) {
        return 'dailyDealCardPackGroups';
      }
      return 'marketCardPackGroups';
    }
  );
  const dailyDealCardPackGroup = dailyDealCardPackGroups?.at(0);

  const { bgImage } = usePackAssets({
    cardPackGroupId: dailyDealCardPackGroup?.id || '',
  });

  return (
    <>
      <PullToRefetch refetch={refetch}>
        <Vertical gap={3}>
          <CommonPacksHeader
            marketRoot={marketRoot}
            refetch={refetch}
            backgroundUrl={bgImage}
            title={<DailyDealsTitle cardPackGroup={dailyDealCardPackGroup} />}
            balances={
              <Horizontal>
                <EssenceBalance
                  value={currentUser?.commonCardPoints || 0}
                  rarity={Rarity.common}
                />
                <GemsBalance
                  balance={currentUser?.gemsBalances[0]?.amount || 0}
                />
              </Horizontal>
            }
          >
            {!!dailyDealCardPackGroup && (
              <DailyDeals
                cardPackGroup={dailyDealCardPackGroup}
                onClickBuy={onPackSelect}
              />
            )}
          </CommonPacksHeader>

          <Container maxWidth={1500}>
            <Vertical gap={3}>
              {marketCardPackGroups?.map(group => (
                <CardPackGroup
                  key={group.id}
                  cardPackGroup={group}
                  onClickBuy={onPackSelect}
                />
              ))}
            </Vertical>
          </Container>
        </Vertical>
      </PullToRefetch>
      {!!openedCardPack && (
        <PackOpeningDialog
          open
          onClose={onPackOpeningDialogClose}
          cardPack={openedCardPack}
          gemsPacks={gemsPacks}
          showPlayerDetails
          starCardPackSlug={
            cardPackGroups
              .find(group =>
                group.cardPacks.some(pack => pack.slug === openedCardPack.slug)
              )
              ?.cardPacks.find(pack => !!pack.rewardedByTask)?.slug
          }
        />
      )}
    </>
  );
};

CommonPacksPage.fragments = {
  marketRoot: gql`
    fragment CommonPacksPage_marketRoot on MarketRoot {
      id
      cardPackGroups(sport: $sport) {
        id
        packConfigGroupType
        cardPacks {
          slug
          ...PackOpeningDialog_cardPack
        }
        ...CardPackGroup_cardPackGroup
        ...usePackOpeningDialog_cardPackGroup
        ...DailyDeals_cardPackGroup
      }
      gemsPacks: inGameCurrencyPacks(sport: $sport, currency: COMMON_GEM) {
        id
        ...PackOpeningDialog_inGameCurrencyPack
      }
      ...CommonPacksHeader_marketRoot
    }
    ${CommonPacksHeader.fragments.marketRoot}
    ${DailyDeals.fragments.cardPackGroup}
    ${usePackOpeningDialog.fragments.cardPackGroup}
    ${CardPackGroup.fragments.cardPackGroup}
    ${PackOpeningDialog.fragments.cardPack}
    ${PackOpeningDialog.fragments.inGameCurrencyPack}
  ` as TypedDocumentNode<CommonPacksPage_marketRoot>,
  tokenRoot: gql`
    fragment CommonPacksPage_tokenRoot on TokenRoot {
      purchasableItems(sport: $sport) {
        slug
        ...PackOpeningDialog_inGameCurrencyPurchasableItem
      }
    }
    ${PackOpeningDialog.fragments.inGameCurrencyPurchasableItem}
  ` as TypedDocumentNode<CommonPacksPage_tokenRoot>,
};
